/**
 * @warning
 * THIS FILE CONTAINS SHARED LOGIC / EXPECTATIONS WITH AI ENGINE.
 * For logic changes, ensure AI/Amir is looped in to any changes that may impact the AI engine.
 */

import type { MemoryConstructionParams, MemoryGroup } from '@timelyapp/integration-cloud-api-spec';
import { DateTime } from 'luxon';

/**
 * Tool IDs that should be ignored when determining the "winning" memory for a time block
 */
const IGNORED_TOOL_IDS = ['location_tracker', 'google_calendar', 'microsoft_outlook_calendar'];

// TODO: Update to new types from List View project
// Define the TimeTrackingInfo and MemoryLoggingMeta types locally
interface TimeTrackingInfo {
  hour_id: number | null;
  project_color?: string;
  is_directly_tracked: boolean;
  is_indirectly_tracked: boolean;
  is_partially_tracked: boolean;
}

// TODO: Update to new types from List View project
export type MemoryLoggingMeta = {
  tracked_time: TimeTrackingInfo[];
  total_tracked_hours: number;
} & Omit<TimeTrackingInfo, 'hour_id'>;

export type MemoryOrGroupWithTimeTrackingMeta<T> = T & {
  meta: MemoryLoggingMeta;
};

// Type definitions
export interface MemoryBlock {
  startTime: DateTime<true>;
  endTime: DateTime<true>;
  summaryMemory: MemoryGroup; // The "winning" memory for this block
  allMemories: MemoryGroup[]; // All memories that overlap with this block

  // Tracking state information
  is_directly_tracked?: boolean;
  is_indirectly_tracked?: boolean;
  consider_tracked?: boolean;

  // Color computation based on tracking state
  displayColor?: string; // The final color with opacity applied
}

export interface MemoryGroupSummary {
  blocks: MemoryBlock[];
}

/**
 * Calculates tracking info and display color for a memory
 * @param memory - The memory to process
 * @returns Object with tracking flags and display color
 */
function getTrackingColorInfo(memory: MemoryGroup) {
  const meta = 'meta' in memory ? (memory.meta as MemoryLoggingMeta) : null;
  const is_directly_tracked = !!meta?.is_directly_tracked;
  const is_indirectly_tracked = !!meta?.is_indirectly_tracked;
  const is_partially_tracked = !!meta?.is_partially_tracked;
  const consider_tracked = is_directly_tracked || is_indirectly_tracked;

  // Calculate opacity based on tracking status
  const opacity = is_directly_tracked ? '80' : '30';

  // Only use project color if the memory is tracked in some way
  const displayColor =
    consider_tracked && meta?.project_color ? `${meta.project_color}${opacity}` : memory.color;

  return { is_directly_tracked, is_indirectly_tracked, consider_tracked, displayColor };
}

/**
 * Takes memory groups and creates time-block based summaries.
 * Each time block can only have one "winning" memory (typically the one with most duration in that block).
 * Blocks align to fixed time intervals (e.g., 30-minute increments) for consistent timeline display.
 *
 * @param memoryGroups - Array of memory groups to summarize
 * @param params - Query parameters including minutesPerBlock, startDate, endDate
 * @returns Object containing blocks of time with their summary memories
 */
export function constructMemoryGroupSummaries(
  memoryGroups: MemoryOrGroupWithTimeTrackingMeta<MemoryGroup>[] | MemoryGroup[],
  params: MemoryConstructionParams
): MemoryGroupSummary {
  // Initialize the result
  const result: MemoryGroupSummary = { blocks: [] };

  // Early return if no memories or blocks are disabled
  if (!memoryGroups.length || params.minutesPerBlock === 0) {
    return result;
  }

  // Extract time range parameters
  const startDateTime = DateTime.fromISO(params.startDate);
  const endDateTime = DateTime.fromISO(params.endDate);
  const minutesPerBlock = params.minutesPerBlock || 30; // Default to 30 minutes if not specified
  const shouldCombineBlocks = params.mergeWithinMinutes && params.mergeWithinMinutes > 0;

  // Helper to get a unique key for a memory
  // TODO: Use memory.v_id once available in the API
  const getMemoryKey = (memory: MemoryGroup): string => `${memory.id}_${memory.title}`;

  // Track which memories are already assigned to a block
  const processedMemories = new Set<string>();
  const processedBlocks = new Set<number>();

  // Round down to the nearest block boundary for consistent visualization
  const roundedStartDateTime = startDateTime.set({
    minute: Math.floor(startDateTime.minute / minutesPerBlock) * minutesPerBlock,
    second: 0,
    millisecond: 0,
  });

  // OPTIMIZATION 1: Pre-parse DateTime objects for all memories
  // This avoids repeatedly parsing the same ISO strings in subsequent operations
  const memoryTimestamps = new Map<string, { start: DateTime; end: DateTime }>();
  memoryGroups.forEach(memory => {
    const memoryKey = getMemoryKey(memory);
    memoryTimestamps.set(memoryKey, {
      start: DateTime.fromISO(memory.started_at),
      end: DateTime.fromISO(memory.ended_at),
    });
  });

  // OPTIMIZATION 2: Create memory lookup by ID for quick access
  const memoriesById = new Map<number, MemoryGroup>();
  memoryGroups.forEach(memory => {
    if (memory.id) {
      memoriesById.set(memory.id, memory);
    }
  });

  // Build all blocks within our time range
  const blocksToProcess: Array<{
    start: DateTime;
    end: DateTime;
    blockMemories: MemoryGroup[];
  }> = [];

  // OPTIMIZATION 3: Use temporal locality to improve findMemoriesInTimeBlock
  // Instead of checking all memories for each block, use a sliding window approach
  // since most memories that are relevant to the current block are likely relevant to adjacent blocks
  let potentialOverlappingMemories = [...memoryGroups];

  let currentBlockStart = roundedStartDateTime;
  while (currentBlockStart < endDateTime) {
    const currentBlockEnd = currentBlockStart.plus({ minutes: minutesPerBlock });

    // Find memories that end before this block starts and remove them from consideration
    if (blocksToProcess.length > 0) {
      potentialOverlappingMemories = potentialOverlappingMemories.filter(memory => {
        const times = memoryTimestamps.get(getMemoryKey(memory));
        return times && times.end > currentBlockStart;
      });
    }

    // Find memories that overlap with this block
    const blockMemories = potentialOverlappingMemories.filter(memory => {
      const times = memoryTimestamps.get(getMemoryKey(memory));
      return times && times.start < currentBlockEnd;
    });

    if (blockMemories.length > 0) {
      blocksToProcess.push({
        start: currentBlockStart,
        end: currentBlockEnd,
        blockMemories,
      });
    }

    currentBlockStart = currentBlockEnd;
  }

  // Sort blocks chronologically to ensure consistent processing order
  const blocksByTime = blocksToProcess
    .map((block, index) => ({ ...block, index }))
    .sort((a, b) => a.start.toMillis() - b.start.toMillis());

  // First pass: identify memories that win in multiple consecutive blocks
  // These can be combined into single extended blocks for better visualization
  const multiBlockMemories: Map<number, number[]> = new Map();

  if (shouldCombineBlocks) {
    // For each memory, check if it spans across multiple blocks
    memoryGroups.forEach(memory => {
      if (processedMemories.has(getMemoryKey(memory))) return;

      const memoryStart = DateTime.fromISO(memory.started_at);
      const memoryEnd = DateTime.fromISO(memory.ended_at);
      const overlappingBlockIndices: number[] = [];

      // Find all blocks where this memory would be the winning memory
      for (let i = 0; i < blocksByTime.length; i++) {
        const block = blocksByTime[i];
        // Check if memory overlaps with this block
        if (memoryStart < block.end && memoryEnd > block.start) {
          // Find the winning memory for this block
          const winningMemory = findWinningMemoryInBlock({
            memories: block.blockMemories,
            blockStart: block.start,
            blockEnd: block.end,
            memoryTimestamps,
          });
          if (winningMemory && winningMemory.id === memory.id) {
            overlappingBlockIndices.push(i);
          }
        }
      }

      // If this memory wins in multiple blocks, check if they're consecutive
      if (overlappingBlockIndices.length > 1) {
        // Sort indices to ensure we check them in order
        overlappingBlockIndices.sort((a, b) => a - b);

        // Check if all block indices are consecutive
        const isConsecutive = overlappingBlockIndices.every((blockIdx, i) => {
          if (i === 0) return true;
          return blockIdx === overlappingBlockIndices[i - 1] + 1;
        });

        if (isConsecutive) {
          multiBlockMemories.set(memory.id, overlappingBlockIndices);
        }
      }
    });

    // Process memories that win in multiple consecutive blocks
    for (const [memoryId, blockIndices] of multiBlockMemories.entries()) {
      const memory = memoryGroups.find(m => m.id === memoryId);
      if (!memory) continue;

      // Mark these blocks as processed
      blockIndices.forEach(index => processedBlocks.add(index));
      processedMemories.add(getMemoryKey(memory));

      // Create a single extended block spanning all consecutive blocks
      const firstBlockIndex = blockIndices[0];
      const lastBlockIndex = blockIndices[blockIndices.length - 1];

      // Get block boundaries
      const blockStart = blocksByTime[firstBlockIndex].start;
      const blockEnd = blocksByTime[lastBlockIndex].end;

      // Get tracking and color information
      const { is_directly_tracked, is_indirectly_tracked, consider_tracked, displayColor } =
        getTrackingColorInfo(memory);

      // Collect all memories from all affected blocks
      const allMemoriesSet = new Set<MemoryGroup>();
      blockIndices.forEach(index => {
        blocksByTime[index].blockMemories.forEach(m => allMemoriesSet.add(m));
      });

      // Add the combined block
      result.blocks.push({
        startTime: blockStart,
        endTime: blockEnd,
        summaryMemory: memory,
        allMemories: Array.from(allMemoriesSet),
        is_directly_tracked,
        is_indirectly_tracked,
        consider_tracked,
        displayColor,
      });
    }
  }

  // Second pass: process remaining blocks individually
  blocksByTime.forEach((block, index) => {
    // Skip blocks already processed in multi-block memories
    if (processedBlocks.has(index)) return;

    // Find eligible memories that overlap with this block
    const eligibleMemories = block.blockMemories.filter(
      memory => !processedMemories.has(getMemoryKey(memory))
    );

    if (eligibleMemories.length > 0) {
      // Find the winning memory for this block
      let winningMemory = findWinningMemoryInBlock({
        memories: eligibleMemories,
        blockStart: block.start,
        blockEnd: block.end,
        memoryTimestamps,
      });

      // Fallback if no eligible winner but there are memories in the block
      if (!winningMemory && block.blockMemories.length > 0) {
        // Find the first memory that isn't from an ignored tool
        winningMemory =
          block.blockMemories.find(
            memory => memory.tool?.id && !IGNORED_TOOL_IDS.includes(memory.tool.id)
          ) || null;
      }

      if (winningMemory) {
        // Mark this memory as processed if we're combining blocks
        if (shouldCombineBlocks) {
          processedMemories.add(getMemoryKey(winningMemory));
        }

        // Get tracking and color information
        const { is_directly_tracked, is_indirectly_tracked, consider_tracked, displayColor } =
          getTrackingColorInfo(winningMemory);

        // Add the block with properly aligned timestamps and tracking info
        result.blocks.push({
          startTime: block.start,
          endTime: block.end,
          summaryMemory: winningMemory,
          allMemories: block.blockMemories,
          is_directly_tracked,
          is_indirectly_tracked,
          consider_tracked,
          displayColor,
        });
      }
    }
  });

  return result;
}

/**
 * Parameters for findWinningMemoryInBlock function
 */
interface FindWinningMemoryParams {
  /** Array of memories that overlap with this block */
  memories: MemoryGroup[];
  /** Start time of the block */
  blockStart: DateTime;
  /** End time of the block */
  blockEnd: DateTime;
  /** Optional map of pre-parsed DateTime objects for optimization */
  memoryTimestamps?: Map<string, { start: DateTime; end: DateTime }>;
}

/**
 * Finds the "winning" memory for a time block based on various factors:
 * 1. Time spent within the block
 * 2. Overall importance/priority of the memory (total_seconds)
 * 3. Whether the memory extends beyond block boundaries
 * 4. Whether the memory is starred (highest priority)
 *
 * @returns The memory that "wins" this block, or null if no memories
 */
function findWinningMemoryInBlock({
  memories,
  blockStart,
  blockEnd,
  memoryTimestamps,
}: FindWinningMemoryParams): MemoryGroup | null {
  if (!memories.length) return null;

  // OPTIMIZATION: Fast path for single memory blocks
  if (
    memories.length === 1 &&
    memories[0].tool?.id &&
    !IGNORED_TOOL_IDS.includes(memories[0].tool.id)
  ) {
    return memories[0];
  }

  // Helper function to get memory start/end times, using pre-parsed values if available
  const getMemoryTimes = (memory: MemoryGroup) => {
    if (memoryTimestamps) {
      const key = `${memory.id}_${memory.title}`;
      const times = memoryTimestamps.get(key);
      if (times) {
        return times;
      }
    }

    // Fallback to parsing ISO strings
    return {
      start: DateTime.fromISO(memory.started_at),
      end: DateTime.fromISO(memory.ended_at),
    };
  };

  // Calculate the block duration once (for use in percentage calculations)
  const blockDuration = blockEnd.diff(blockStart).as('seconds');

  // Build the array of memories with their scores
  const memoriesWithScores = memories
    .filter(memory => {
      // Allow starred memories even if they're from (forced) IGNORED_TOOL_IDS
      return memory.starred || (memory.tool?.id && !IGNORED_TOOL_IDS.includes(memory.tool.id));
    })
    .map(memory => {
      const { start: memoryStart, end: memoryEnd } = getMemoryTimes(memory);

      // Calculate overlap duration
      const overlapStart = memoryStart > blockStart ? memoryStart : blockStart;
      const overlapEnd = memoryEnd < blockEnd ? memoryEnd : blockEnd;
      const durationInBlock = Math.max(0, overlapEnd.diff(overlapStart).as('seconds'));

      // Calculate block coverage percentage
      const percentageOfBlock = blockDuration > 0 ? durationInBlock / blockDuration : 0;

      // Calculate extension score (bonus for memories extending outside the block)
      const extendsBefore = memoryStart < blockStart;
      const extendsAfter = memoryEnd > blockEnd;
      const extensionScore = (extendsBefore ? 1 : 0) + (extendsAfter ? 1 : 0);

      // Total importance/intensity of the memory
      const totalSeconds = memory.total_seconds || 0;

      // Add a significant boost for starred memories
      const starBonus = memory.starred ? 1000 : 0; // Large enough to override other factors

      // Combined significance score for decision making
      const significanceScore =
        percentageOfBlock * 50 + // Block coverage (50% weight)
        extensionScore * 10 + // Extension bonus
        totalSeconds / 100 + // Overall importance
        starBonus; // Star bonus (highest priority)

      return {
        memory,
        durationInBlock,
        percentageOfBlock,
        extensionScore,
        totalSeconds,
        significanceScore,
        isStarred: memory.starred,
      };
    });

  // Use the same sorting criteria as the original implementation to ensure consistent behavior
  memoriesWithScores.sort((a, b) => {
    // Criteria 0: Starred memories always win
    if (a.isStarred && !b.isStarred) return -1;
    if (!a.isStarred && b.isStarred) return 1;

    // If both are starred or both are not starred, use existing criteria
    // Criteria 1: If one memory has significantly more importance, it wins
    const totalSecondsRatio = (b.totalSeconds || 1) / (a.totalSeconds || 1);
    if (
      a.totalSeconds > 0 &&
      b.totalSeconds > 0 &&
      (totalSecondsRatio >= 1.8 || totalSecondsRatio <= 0.56)
    ) {
      return b.totalSeconds - a.totalSeconds;
    }

    // Criteria 2: If one memory has substantially more coverage, it wins
    const durationDiffPercentage = Math.abs(b.percentageOfBlock - a.percentageOfBlock);
    if (durationDiffPercentage > 0.05) {
      return b.durationInBlock - a.durationInBlock;
    }

    // Criteria 3: Use combined significance score for close cases
    return b.significanceScore - a.significanceScore;
  });

  // Return the highest scoring memory
  return memoriesWithScores[0]?.memory || null;
}
